import { AutomaticWorkflowStep } from "./Steps/AutomaticWorkflowStep";
import { BaseWorkflowStep } from "./Steps/BaseWorkflowStep";
import { CancellableWorkflowStep } from "./Steps/CancellableWorkflowStep";

/**
 * The generic abstract class BaseWorkflow offers a template for complex workflows.
 * Steps are configured by configuring the BaseWorkflowStep.
 * It features the options to:
 *  - Dynamically validate if a step in the workflow should occur or be skipped (isValid)
 *  - Trigger a step in the chain automatically upon previous step completion -> extend AutomaticWorkflowStep
 *  - Define an action for each step if it is cancelled (useful for mandatory or optional steps) -> extend CancellableStep
 *  - Trigger onCompletedWorkflow once all the steps in the workflow are validated and completed
 */
abstract class BaseWorkflow<T> {
    protected stepsQueue: BaseWorkflowStep<T>[];
    protected currentStep: BaseWorkflowStep<T>;

    constructor(stepsQueue: BaseWorkflowStep<T>[]) {
        if (!stepsQueue?.any())
            throw new Error("Can't create a workflow with no queued steps.");
        this.stepsQueue = stepsQueue;
    }

    protected enqueueStep(step: BaseWorkflowStep<T>): void {
        this.stepsQueue.push(step);
    }

    public nextStep(): void {
        let previousStep = this.stepsQueue.shift();
        while (!this.isWorkflowEmpty() && !this.stepsQueue[0].isValid()) { // Find the next valid step
            previousStep = this.stepsQueue.shift();
        }
        this.currentStep = this.stepsQueue[0]; // Set the currentStep reference

        this.triggerAutomaticNextStep();
        this.triggerOnWorkflowCompleted(previousStep);
    }

    protected isWorkflowEmpty(): boolean {
        return !this.stepsQueue.any();
    }

    protected triggerOnWorkflowCompleted(previousStep: BaseWorkflowStep<T>): void {
        if (previousStep && this.isWorkflowEmpty())
            this.onCompletedWorkflow();
    }

    protected triggerAutomaticNextStep(): void {
        if (this.currentStep instanceof AutomaticWorkflowStep)
            this.currentStep.execute();
    }

    public startWorkflow(): void {
        if (!this.stepsQueue[0]?.isValid())
            this.nextStep();
        else {
            this.currentStep = this.stepsQueue[0];
            this.triggerAutomaticNextStep();
        }
    }

    public getCurrentStep(): BaseWorkflowStep<T> {
        return this.currentStep;
    }

    public completeStep(stepToComplete: T): void {
        if (this.currentStep.stepEnum !== stepToComplete)
            throw new Error("Cannot continue to next step of the Workflow, cannot complete a step that is not current.");

        if (!this.currentStep.isCompleted())
            throw new Error("Cannot continue to next step of the Workflow, the step is not completed.");

        this.nextStep();
    }

    public cancelStep(stepToCancel: T): void {
        if (this.currentStep.stepEnum !== stepToCancel)
            throw new Error("Cannot cancel step, the stepToCancel does not correspond to the current step of the Workflow.");

        if (!(this.currentStep instanceof CancellableWorkflowStep))
            throw new Error("Cannot cancel step, the the step is not instanceof CancellableWorkflowStep.");

        this.currentStep.cancel();
    }

    protected abstract onCompletedWorkflow(): void; // Called once the steps are all completed, i.e. the queue is empty
}

export { BaseWorkflow };
